export interface Simulation {
  is_started: boolean;
  title: string;
  description: string;
  date_start: number;
  date_end: number;
  sites: string[];
  scenarios: Scenario[];
}

export interface Scenario {
  id: string;
  sequence_id: number;
  is_selected: boolean;
  settings: ScenarioSettings;
  data: WipTree;
}

export interface ZipScenario {
  id: string;
  sequence_id: number;
  is_selected: boolean;
  settings: ZipScenarioSettings;
  data: WipTree;
}

export interface ScenarioSettingsParameters {
  Fixed_WaitOnBatchTimeAsFixedHours: number;
  Variable_WaitOnBatchTimeAsProcessTimePercentage: number;
  DropDown_JobsPrioritisation: number;
  DropDown_UseOfAdditionalEquipment: boolean;
  DropDown_WaitOnBatchTime: number;
  DropDown_UseEachPOStartDate: boolean;
  DropDown_FileSelection: number;
  Batch_Fill_Grade_Percentage: number;
  DropDown_Use_Storage: boolean;
  DropDown_Use_Personnel_Module: boolean;
  Batch_Grouping_Option: number;
  DropDown_Worker_Pooling: number;
  DropDown_Scrap_Trigger: number;
  DropDown_Process_Time_Trigger: number;
  DropDown_Scrap_Delete: boolean;
  Variable_Process_Time: boolean;
  DropDown_Use_Area_Calculation: boolean;
  DropDown_Storage_Blocking: boolean;
}

export interface ScenarioSettingsParametersOptions {
  [key: string]: {
    key: number;
    value: string | number | boolean;
  }[];
}

export interface ScenarioSettings {
  title: string;
  description: string;
  parameters: ScenarioSettingsParameters[];
  options: ScenarioSettingsParametersOptions;
  run_separated: boolean;
}

export interface ZipScenarioSettings {
  title: string;
  description: string;
  parameters: Partial<ScenarioSettingsParameters>[];
  run_separated: boolean;
}

type WipItemType = 'id' | 'text' | 'time' | 'real';
export type WipItem = {
  value: string;
  type?: WipItemType;
  originalValue?: string;
};

export const uneditableFields = [
  'SITE_ABBREVIATION',
  'SITE',
  'OPERATION_BUSINESS',
  'SU',
  'MO',
  'TU',
  'WE',
  'TH',
  'FR',
  'SA',
  'SHIFT_CALENDAR',
  'SHIFT_HANDOVER_START_TIME',
  'SHIFT_HANDOVER_END_TIME',
  'BATCH_BLK_SUFFIX',
  'MAX_QUANTITY_IN_BATCH',
  'END_PRODUCT',
  'END_PRODUCTION_ORDER',
  'PARENT_PRODUCT',
  'PARENT_PRODUCTION_ORDER',
  'RESOURCE_MAX_CAPACITY',
  'RESOURCE_MTTR_ILLNESS',
  'RESOURCE_MTTR_TECH',
  'RESOURCE_MTTR_VACATION',
  'RESOURCE_SHIFT_CALENDAR',
  'RESOURCE_AVAILABILITY_ILLNESS',
  'RESOURCE_AVAILABILITY_TECH',
  'RESOURCE_AVAILABILITY_VACATION',
  'PRODUCT_SUBCLASS',
  'OPERATION_INITIAL_START_TIME',
  'OPERATION_INITIAL_DURATION',
  'OPERATION_MIDDLE_START_TIME',
  'OPERATION_MIDDLE_DURATION',
  'OPERATION_END_START_TIME',
  'OPERATION_END_DURATION',
  'PO_LAST_STEP_WIP',
  'STEP',
  'RESOURCE_GOOD',
  'RESOURCE_SCRAP',
  'RESOURCE_REWORK',
  'OPERATION_GOOD',
  'OPERATION_SCRAP',
  'OPERATION_REWORK',
  'PRODUCTION_ORDER',
  'key',
  'is_disabled',
  'is_wip',
  'is_deleted',
  'is_clicked',
  'is_selectable',
  'is_selected',
  'is_hidden',
  'is_match',
  'is_open',
  'parent',
  'children',
];

export type WipItemParams = {
  key: string;
  title: string;
  is_disabled: boolean;
  is_wip: boolean;
  is_deleted: boolean;
  is_clicked: boolean;
  is_selectable: boolean;
  is_selected: boolean;
  is_hidden: boolean;
  is_match: boolean;
  is_open: boolean;
  parent: string;
  children: string[];
};

export interface ShiftCalendar extends WipItemParams {
  RESOURCE_SHIFT_CALENDAR_ID: WipItem;
  RESOURCE_SHIFT_CALENDAR: WipItem;
  SITE: WipItem;
  SITE_ABBREVIATION: WipItem;
  SITE_ID: WipItem;
}
export interface ShiftCalendarTree extends WipItemParams {
  RESOURCE_SHIFT_CALENDAR_ID: WipItem;
  RESOURCE_SHIFT_CALENDAR: WipItem;
  SITE: WipItem;
  SITE_ABBREVIATION: WipItem;
  SITE_ID: WipItem;
}

export interface Shift extends WipItemParams {
  END_TIME: WipItem;
  SU: WipItem;
  MO: WipItem;
  TU: WipItem;
  WE: WipItem;
  TH: WipItem;
  FR: WipItem;
  SA: WipItem;
  SHIFT: WipItem;
  SHIFT_CALENDAR: WipItem;
  SHIFT_CALENDAR_ID: WipItem;
  SHIFT_ID: WipItem;
  SITE: WipItem;
  SITE_ABBREVIATION: WipItem;
  SITE_ID: WipItem;
  START_TIME: WipItem;
  SHIFT_HANDOVER_START_TIME: WipItem;
  SHIFT_HANDOVER_END_TIME: WipItem;
}
export interface ShiftTree extends WipItemParams {
  SHIFT_HANDOVER_END_TIME: TreeItem;
  SHIFT_HANDOVER_START_TIME: TreeItem;
  END_TIME: TreeItem;
  SU: TreeItem;
  MO: TreeItem;
  TU: TreeItem;
  WE: TreeItem;
  TH: TreeItem;
  FR: TreeItem;
  SA: TreeItem;
  SET_OF_PAUSE_ID: TreeItem;
  SHIFT: TreeItem;
  SHIFT_CALENDAR_ID: TreeItem;
  SHIFT_ID: TreeItem;
  SITE_ID: TreeItem;
  START_TIME: TreeItem;
}

export interface Shutdown extends WipItemParams {
  REDUCED_ON: WipItem;
  REDUCED_TIME: WipItem;
  SHUTDOWN_DATE: WipItem;
  SHUTDOWN_ID: WipItem;
  SHUTDOWN_TITLE: WipItem;
  SHUTDOWN_TYPE: WipItem;
  SIMULATION_ID: WipItem;
  SITE: WipItem;
  SITE_ABBREVIATION: WipItem;
  SITE_ID: WipItem;
  WORKING_OR_NOT: WipItem;
}
export interface ShutdownTree extends WipItemParams {
  REDUCED_ON: TreeItem<number>;
  REDUCED_TIME: TreeItem<number>;
  SHUTDOWN_DATE: TreeItem<number>;
  SHUTDOWN_ID: TreeItem;
  SHUTDOWN_TITLE: TreeItem;
  SHUTDOWN_TYPE: TreeItem;
  SITE_ID: TreeItem;
  WORKING_OR_NOT: TreeItem<number>;
}

export interface AdditionalEquipment extends WipItemParams {
  ADDITIONAL_EQUIPMENT: WipItem;
  ADDITIONAL_EQUIPMENT_AVAILABILITY_ILLNESS: WipItem;
  ADDITIONAL_EQUIPMENT_AVAILABILITY_TECH: WipItem;
  ADDITIONAL_EQUIPMENT_AVAILABILITY_VACATION: WipItem;
  ADDITIONAL_EQUIPMENT_CLASS: WipItem;
  ADDITIONAL_EQUIPMENT_GRADE: WipItem;
  ADDITIONAL_EQUIPMENT_ID: WipItem;
  ADDITIONAL_EQUIPMENT_MAX_CAPACITY: WipItem;
  ADDITIONAL_EQUIPMENT_MTTR_ILLNESS: WipItem;
  ADDITIONAL_EQUIPMENT_MTTR_TECH: WipItem;
  ADDITIONAL_EQUIPMENT_MTTR_VACATION: WipItem;
  ADDITIONAL_EQUIPMENT_SUB_CLASS: WipItem;
  SHIFT_CALENDAR: WipItem;
  SHIFT_CALENDAR_ID: WipItem;
  SITE: WipItem;
  SITE_ABBREVIATION: WipItem;
  SITE_ID: WipItem;
}

export interface Pause extends WipItemParams {
  END_TIME: WipItem;
  PAUSE_ID: WipItem;
  SITE: WipItem;
  SITE_ABBREVIATION: WipItem;
  SITE_ID: WipItem;
  START_TIME: WipItem;
}

export interface SetAdditionalEquipment extends WipItemParams {
  ADDITIONAL_EQUIPMENT: WipItem;
  ADDITIONAL_EQUIPMENT_ID: WipItem;
  ADDITIONAL_EQUIPMENT_PRIORITY: WipItem;
  SET_OF_ADDITIONAL_EQUIPMENT_ID: WipItem;
  SITE: WipItem;
  SITE_ABBREVIATION: WipItem;
  SITE_ID: WipItem;
}

export interface SetWorkUnit extends WipItemParams {
  SET_OF_WORK_UNIT_ID: WipItem;
  SITE: WipItem;
  SITE_ID: WipItem;
  SITE_ABBREVIATION: WipItem;
  WORK_UNIT: WipItem;
  WORK_UNIT_ID: WipItem;
  WORK_UNIT_PRIORITY: WipItem;
}

export interface WorkUnit extends WipItemParams {
  SHIFT_CALENDAR: WipItem;
  SHIFT_CALENDAR_ID: WipItem;
  SITE: WipItem;
  SITE_ABBREVIATION: WipItem;
  SITE_ID: WipItem;
  WORK_UNIT: WipItem;
  WORK_UNIT_AVAILABILITY_ILLNESS: WipItem;
  WORK_UNIT_AVAILABILITY_TECH: WipItem;
  WORK_UNIT_AVAILABILITY_VACATION: WipItem;
  WORK_UNIT_CLASS: WipItem;
  WORK_UNIT_GRADE: WipItem;
  WORK_UNIT_ID: WipItem;
  WORK_UNIT_MAX_CAPACITY: WipItem;
  WORK_UNIT_MTTR_ILLNESS: WipItem;
  WORK_UNIT_MTTR_TECH: WipItem;
  WORK_UNIT_MTTR_VACATION: WipItem;
  WORK_UNIT_SUB_CLASS: WipItem;
}

export interface Product extends WipItemParams {
  END_PRODUCT: WipItem;
  END_PRODUCT_ID: WipItem;
  PARENT_PRODUCT: WipItem;
  PARENT_PRODUCT_ID: WipItem;
  PRODUCT: WipItem;
  PRODUCT_CLASS: WipItem;
  PRODUCT_SUBCLASS: WipItem;
  PRODUCT_ID: WipItem;
  SITE: WipItem;
  SITE_ABBREVIATION: WipItem;
  SITE_ID: WipItem;
}

export interface TreeItem<T = string> {
  value: T;
  originalValue: T;
}

export interface ProductTree extends WipItemParams {
  END_PRODUCT_ID: TreeItem;
  PARENT_PRODUCT_ID: TreeItem;
  PRODUCT: TreeItem;
  PRODUCT_CLASS: TreeItem;
  PRODUCT_ID: TreeItem;
  SITE_ID: TreeItem;
}

export interface Operation extends WipItemParams {
  BATCH_BLK_SUFFIX: WipItem;
  OPERATION: WipItem;
  OPERATION_BUSINESS: WipItem;
  OPERATION_ID: WipItem;
  OPERATION_TYPE: WipItem;
  PROCESS: WipItem;
  SITE: WipItem;
  SITE_ABBREVIATION: WipItem;
  SITE_ID: WipItem;
  STEP: WipItem;
  WORK_PLAN: WipItem;
  WORK_PLAN_ID: WipItem;
  PROCESS_TYPE_ID: WipItem;
  OPERATION_TYPE_ID: WipItem;
  OPERATION_INITIAL_START_TIME: WipItem;
  OPERATION_INITIAL_DURATION: WipItem;
  OPERATION_MIDDLE_START_TIME: WipItem;
  OPERATION_MIDDLE_DURATION: WipItem;
  OPERATION_END_START_TIME: WipItem;
  OPERATION_END_DURATION: WipItem;
  OPERATION_GOOD: WipItem;
  OPERATION_SCRAP: TreeItem;
  OPERATION_REWORK: TreeItem;
}

export interface OperationTree extends WipItemParams {
  PO_LAST_STEP_WIP: TreeItem;
  BATCH_BLK_SUFFIX: TreeItem;
  MAX_QUANTITY_IN_BATCH: TreeItem;
  OPERATION: TreeItem;
  OPERATION_BUSINESS: TreeItem;
  OPERATION_ID: TreeItem;
  OPERATION_TYPE: TreeItem;
  OPERATION_TYPE_ID: TreeItem;
  PROCESS_TYPE_ID: TreeItem;
  SITE: TreeItem;
  SITE_ABBREVIATION: TreeItem;
  SITE_ID: TreeItem;
  STEP: TreeItem;
  WORK_PLAN: TreeItem;
  WORK_PLAN_ID: TreeItem;
  OPERATION_GOOD: TreeItem;
  OPERATION_SCRAP: TreeItem;
  OPERATION_REWORK: TreeItem;
}

export interface ProductionOrder extends WipItemParams {
  END_PRODUCT: WipItem;
  END_PRODUCTION_ORDER: WipItem;
  END_PRODUCTION_ORDER_ID: WipItem;
  END_PRODUCT_ID: WipItem;
  PARENT_PRODUCT: WipItem;
  PARENT_PRODUCTION_ORDER: WipItem;
  PARENT_PRODUCTION_ORDER_ID: WipItem;
  PARENT_PRODUCT_ID: WipItem;
  PO_END_DATE_TIMESTAMP: WipItem;
  PO_LAST_STEP_WIP: WipItem;
  PO_QUANTITY: WipItem;
  PO_START_DATE_TIMESTAMP: WipItem;
  PRODUCT: WipItem;
  PRODUCTION_ORDER: WipItem;
  PRODUCTION_ORDER_ID: WipItem;
  PRODUCT_ID: WipItem;
  QUANTITY_ASSEMBLED_PARTS: WipItem;
  SITE: WipItem;
  SITE_ABBREVIATION: WipItem;
  SITE_ID: WipItem;
  WORK_PLAN: WipItem;
  WORK_PLAN_ID: WipItem;
}

export interface ProductionOrderTree extends WipItemParams {
  END_PRODUCTION_ORDER_ID: TreeItem;
  END_PRODUCT_ID: TreeItem;
  PARENT_PRODUCTION_ORDER_ID: TreeItem;
  PO_END_DATE_TIMESTAMP: TreeItem<number>;
  PO_START_DATE_TIMESTAMP: TreeItem<number>;
  PRODUCT_ID: TreeItem;
  PRODUCTION_ORDER: TreeItem;
  PRODUCTION_ORDER_ID: TreeItem;
  QUANTITY_ASSEMBLED_PARTS: TreeItem<number>;
  PO_LAST_STEP_WIP: TreeItem<number>;
  PO_QUANTITY: TreeItem<number>;
  SITE_ID: TreeItem;
  WORK_PLAN_ID: TreeItem;
}

export interface WorkPlan extends WipItemParams {
  SCENARIO_ID: WipItem;
  SIMULATION_ID: WipItem;
  SITE: WipItem;
  SITE_ABBREVIATION: WipItem;
  SITE_ID: WipItem;
  WORK_PLAN: WipItem;
  WORK_PLAN_ID: WipItem;
}
export interface WorkPlanTree extends WipItemParams {
  SITE_ID: TreeItem;
  WORK_PLAN: TreeItem;
  WORK_PLAN_ID: TreeItem;
}

export interface Site extends WipItemParams {
  SITE: WipItem;
  SITE_ABBREVIATION: WipItem;
  SITE_ID: WipItem;
  ENTERPRISE_ID: WipItem;
  CLUSTER_LEAD: WipItem;
  CITY: WipItem;
  COUNTRY: WipItem;
}
export interface SiteTree extends WipItemParams {
  SITE: TreeItem;
  SITE_ABBREVIATION: TreeItem;
  SITE_ID: TreeItem;
  ENTERPRISE_ID: TreeItem;
  CLUSTER_LEAD: TreeItem;
}

export interface WorkPlanProcess extends WipItemParams {
  PROCESS: WipItem;
  PROCESS_ID: WipItem;
}
export interface WorkUnitClass extends WipItemParams {
  CLASS: WipItem;
  CLASS_ID: WipItem;
  PARENT_CLASS_ID: WipItem;
}
export interface AdditionalEquipmentClass extends WipItemParams {
  CLASS: WipItem;
  CLASS_ID: WipItem;
  PARENT_CLASS_ID: WipItem;
}

export interface ProductClass extends WipItemParams {
  PRODUCT_CLASS: WipItem;
  PRODUCT_CLASS_ID: WipItem;
  SITE_ID: WipItem;
}
export interface ResouceBuilding extends WipItemParams {
  BUILDING: WipItem;
  BUILDING_ID: WipItem;
  SITE_ID: WipItem;
  WIP_ID: WipItem;
}
export interface Enterprise extends WipItemParams {
  ENTERPRISE: WipItem;
  ENTERPRISE_ID: WipItem;
}

export interface OperationResource extends WipItemParams {
  OPERATIONS_RESOURCES_ID: WipItem;
  OPERATION_ID: WipItem;
  RESOURCE_ID: WipItem;
  RESOURCE_PRIORITY: WipItem;
  RESOURCE_TYPE_ID: WipItem;
  MAX_QUANTITY_IN_BATCH: WipItem;
  SETUP_TIME: WipItem;
  PROCESS_TIME: WipItem;
  PERSONNEL_TIME: WipItem;
  SITE_ID: WipItem;
  WIP_ID: WipItem;
}
export interface PauseShift extends WipItemParams {
  SHIFT_PAUSE_PK: WipItem;
  PAUSE_ID: WipItem;
  SHIFT_ID: WipItem;
  SITE_ID: WipItem;
  SITE: WipItem;
}
export interface PauseShiftTree extends WipItemParams {
  PAUSE_SHIFT_ID: TreeItem;
  PAUSE_ID: TreeItem;
  SHIFT_ID: TreeItem;
  SITE_ID: TreeItem;
}

export interface Resource extends WipItemParams {
  RESOURCE_NAME: WipItem;
  RESOURCE_ID: WipItem;
  RESOURCE_ID_BK: WipItem;
  BUILDING_ID: WipItem;
  RESOURCE_AVAILABILITY_ILLNESS: WipItem;
  RESOURCE_AVAILABILITY_TECH: WipItem;
  RESOURCE_AVAILABILITY_VACATION: WipItem;
  RESOURCE_CLASS_ID: WipItem;
  RESOURCE_GRADE_ID: WipItem;
  RESOURCE_MAX_CAPACITY: WipItem;
  RESOURCE_MTTR_ILLNESS: WipItem;
  RESOURCE_MTTR_TECH: WipItem;
  RESOURCE_MTTR_VACATION: WipItem;
  RESOURCE_SHIFT_CALENDAR_ID: WipItem;
  RESOURCE_SHIFT_CALENDAR: WipItem;
  RESOURCE_SUBCLASS_ID: WipItem;
  SITE_ID: WipItem;
  RESOURCE_CLASS: WipItem;
  RESOURCE_GOOD: WipItem;
  RESOURCE_SCRAP: WipItem;
  RESOURCE_REWORK: WipItem;
  RESOURCE_MAX_WORKER_CAPACITY: WipItem;
  TS_FACTOR: TreeItem;
}
export interface ResourceTree extends WipItemParams {
  RESOURCE_NAME: TreeItem;
  RESOURCE_ID: TreeItem;
  BUILDING_ID: TreeItem;
  RESOURCE_AVAILABILITY_ILLNESS: TreeItem;
  RESOURCE_AVAILABILITY_TECH: TreeItem;
  RESOURCE_AVAILABILITY_VACATION: TreeItem;
  RESOURCE_CLASS_ID: TreeItem;
  RESOURCE_GRADE_ID: TreeItem;
  RESOURCE_MAX_CAPACITY: TreeItem;
  RESOURCE_MTTR_ILLNESS: TreeItem;
  RESOURCE_MTTR_TECH: TreeItem;
  RESOURCE_MTTR_VACATION: TreeItem;
  RESOURCE_SHIFT_CALENDAR_ID: TreeItem;
  RESOURCE_SHIFT_CALENDAR: TreeItem;
  RESOURCE_SUBCLASS_ID: TreeItem;
  SITE_ID: TreeItem;
  RESOURCE_GOOD: WipItem;
  RESOURCE_SCRAP: WipItem;
  RESOURCE_REWORK: WipItem;
  RESOURCE_MAX_WORKER_CAPACITY: WipItem;
  TS_FACTOR: TreeItem;
}
export interface ResourceGrade extends WipItemParams {
  RESOURCE_GRADE: WipItem;
  RESOURCE_GRADE_ID: WipItem;
  SITE_ID: WipItem;
}
export interface ResourceType extends WipItemParams {
  RESOURCE_TYPE: WipItem;
  RESOURCE_TYPE_ID: WipItem;
  SITE_ID: WipItem;
}
export interface ResourceTypeTree extends WipItemParams {
  RESOURCE_TYPE: TreeItem;
  RESOURCE_TYPE_ID: TreeItem;
  SITE_ID: TreeItem;
}
export interface ResourceName extends WipItemParams {
  RESOURCE_NAME: WipItem;
  RESOURCE_NAME_ID: WipItem;
  SITE_ID: WipItem;
  WIP_ID: WipItem;
}
export interface ResourceLifecycle extends WipItemParams {
  RESOURCE_LIFECYCLE_ID: WipItem;
  RESOURCE_ID: WipItem;
  RESOURCE_LIFECYCLE_STATUS_ID: WipItem;
  RESOURCE_LIFECYCLE_STATUS: WipItem;
  RESOURCE_LIFECYCLE_STATUS_START_TIMESTAMP: WipItem;
  RESOURCE_LIFECYCLE_STATUS_END_TIMESTAMP: WipItem;
  RESOURCE_LIFECYCLE_STATUS_DISTANCE: WipItem;
  RESOURCE_LIFECYCLE_STATUS_MOVED_RESOURCE_ID: WipItem;
  SITE_ID: WipItem;
  SITE: WipItem;
  SITE_ABBREVIATION: WipItem;
}
export interface ResourceLifecycleStatus extends WipItemParams {
  RESOURCE_LIFECYCLE_STATUS_ID: WipItem;
  RESOURCE_LIFECYCLE_STATUS: WipItem;
  SITE_ID: WipItem;
}
export interface ResourceClass extends WipItemParams {
  RESOURCE_CLASS: WipItem;
  RESOURCE_CLASS_ID: WipItem;
  RESOURCE_TYPE_ID: WipItem;
  SITE_ID: WipItem;
}
export interface ResourceClassTree extends WipItemParams {
  RESOURCE_CLASS: TreeItem;
  RESOURCE_CLASS_ID: TreeItem;
  SITE_ID: WipItem;
}
export interface ResourceSubClass extends WipItemParams {
  RESOURCE_SUBCLASS: WipItem;
  RESOURCE_SUBCLASS_ID: WipItem;
  RESOURCE_CLASS_ID: WipItem;
  RESOURCE_CLASS: WipItem;
  SITE_ID: WipItem;
  WIP_ID: WipItem;
}
export interface ResourceSubClassTree extends WipItemParams {
  RESOURCE_SUBCLASS: WipItem;
  RESOURCE_SUBCLASS_ID: WipItem;
  RESOURCE_CLASS_ID: WipItem;
  RESOURCE_CLASS: WipItem;
  SITE_ID: WipItem;
}
export interface ProcessTypes extends WipItemParams {
  PROCESS: WipItem;
  PROCESS_TYPE_ID: WipItem;
  SITE_ID: WipItem;
}
export interface ProcessTypesTree extends WipItemParams {
  PROCESS: TreeItem;
  PROCESS_TYPE_ID: TreeItem;
  SITE_ID: TreeItem;
}
export interface OperationTypes extends WipItemParams {
  OPERATION: WipItem;
  OPERATION_TYPE_ID: WipItem;
  SITE_ID: WipItem;
}
export interface OperationTypesTree extends WipItemParams {
  OPERATION: TreeItem;
  OPERATION_TYPE_ID: TreeItem;
  SITE_ID: TreeItem;
}
export interface Skills extends WipItemParams {
  SKILL_ID: WipItem;
  SKILL: WipItem;
  SITE_ID: WipItem;
}

export interface Personnel extends WipItemParams {
  PERSON_ID: WipItem;
  PERSON_NAME: WipItem;
  SITE: WipItem;
  SITE_ID: WipItem;
  WIP_ID: WipItem;
}

export interface PersonnelGroup extends WipItemParams {
  PERSONNEL_GROUP_PK: WipItem;
  PERSONNEL_GROUP: WipItem;
  PERSONNEL_GROUP_ID: WipItem;
  RESOURCE_CLASS_ID: WipItem;
  RESOURCE_ID: WipItem;
  RESOURCE_SUBCLASS_ID: WipItem;
  SITE: WipItem;
  SITE_ABBREVIATION: WipItem;
  SITE_ID: WipItem;
  WIP_ID: WipItem;
}

export interface DistinctPersonnelGroup extends WipItemParams {
  PERSONNEL_GROUP: WipItem;
  PERSONNEL_GROUP_ID: WipItem;
  SITE_ID: WipItem;
}

export interface PersonnelSkills extends WipItemParams {
  PERSON_SKILL_PK: WipItem;
  PERSON_ID: WipItem;
  PERSON_SKILL_PRIORITY: WipItem;
  SITE: WipItem;
  SITE_ABBREVIATION: WipItem;
  SITE_ID: WipItem;
  SKILL_ID: WipItem;
  SKILL: WipItem;
  WIP_ID: WipItem;
}
export interface OperationsSkills extends WipItemParams {
  OPERATIONS_SKILL_PK: TreeItem;
  OPERATION_ID: TreeItem;
  OPERATION_SKILL_MIN_WORKERS: TreeItem;
  OPERATION_SKILL_PRIORITY: TreeItem;
  SITE: TreeItem;
  SITE_ABBREVIATION: TreeItem;
  SITE_ID: WipItem;
  SKILL_ID: WipItem;
  WIP_ID: WipItem;
}

export interface PersonnelByGroups extends WipItemParams {
  PERSON_BY_GROUP_PK: WipItem;
  PERSONNEL_GROUP_ID: TreeItem;
  PERSON_BY_GROUP_ID: TreeItem;
  PERSON_ID: TreeItem;
  SITE: TreeItem;
  SITE_ABBREVIATION: TreeItem;
  SITE_ID: WipItem;
  WIP_ID: WipItem;
}

export interface Wip {
  additional_equipments: AdditionalEquipment[];
  operations: Operation[];
  production_orders: ProductionOrder[];
  products: Product[];
  set_additional_equipments: SetAdditionalEquipment[];
  set_work_units: SetWorkUnit[];
  shift_calendar: ShiftCalendar[];
  shifts: Shift[];
  shutdowns: Shutdown[];
  work_plans: WorkPlan[];
  sites: Site[];
  workplan_processes: WorkPlanProcess[];
  workunit_classes: WorkUnitClass[];
  additionalequipment_classes: AdditionalEquipmentClass[];
  process_types: ProcessTypes[];
  operation_types: OperationTypes[];
  resources: Resource[];
  resource_lifecycle: ResourceLifecycle[];
  resource_lifecycle_status: ResourceLifecycleStatus[];
  resource_names: ResourceName[];
  resource_types: ResourceType[];
  resource_classes: ResourceClass[];
  resource_sub_classes: ResourceSubClass[];
  resource_grades: ResourceGrade[];
  operations_resources: OperationResource[];
  pause_shift: PauseShift[];
  enterprises: Enterprise[];
  product_classes: ProductClass[];
  resource_buildings: ResouceBuilding[];
  skills: Skills[];
  personnel: Personnel[];
  personnel_group: PersonnelGroup[];
  personnel_skills: PersonnelSkills[];
  operations_skills: OperationsSkills[];
  personnel_by_groups: PersonnelByGroups[];
  pauses: Pause[];
  distinct_personnel_group: DistinctPersonnelGroup[];
}

export interface WipTreeElement<T> {
  allIds: string[];
  byId: { [key: string]: T };
}

export interface WipTree {
  additional_equipments: WipTreeElement<AdditionalEquipment>;
  operations: WipTreeElement<OperationTree>;
  production_orders: WipTreeElement<ProductionOrderTree>;
  products: WipTreeElement<ProductTree>;
  set_additional_equipments: WipTreeElement<SetAdditionalEquipment>;
  set_work_units: WipTreeElement<SetWorkUnit>;
  shift_calendar: WipTreeElement<ShiftCalendarTree>;
  shifts: WipTreeElement<ShiftTree>;
  shutdowns: WipTreeElement<ShutdownTree>;
  work_plans: WipTreeElement<WorkPlanTree>;
  work_units: WipTreeElement<WorkUnit>;
  sites: WipTreeElement<SiteTree>;
  workplan_processes: WipTreeElement<WorkPlanProcess>;
  workunit_classes: WipTreeElement<WorkUnitClass>;
  additionalequipment_classes: WipTreeElement<AdditionalEquipmentClass>;
  resources: WipTreeElement<ResourceTree>;
  process_types: WipTreeElement<ProcessTypesTree>;
  operation_types: WipTreeElement<OperationTypes>;
  resource_grades: WipTreeElement<ResourceGrade>;
  resource_buildings: WipTreeElement<ResouceBuilding>;
  resource_classes: WipTreeElement<ResourceClassTree>;
  resource_sub_classes: WipTreeElement<ResourceSubClassTree>;
  resource_types: WipTreeElement<ResourceTypeTree>;
  operations_resources: WipTreeElement<OperationResource>;
  pause_shift: WipTreeElement<PauseShift>;
  resource_lifecycle: WipTreeElement<ResourceLifecycle>;
  resource_lifecycle_status: WipTreeElement<ResourceLifecycleStatus>;
  skills: WipTreeElement<Skills>;
  personnel: WipTreeElement<Personnel>;
  personnel_group: WipTreeElement<PersonnelGroup>;
  personnel_skills: WipTreeElement<PersonnelSkills>;
  operations_skills: WipTreeElement<OperationsSkills>;
  personnel_by_groups: WipTreeElement<PersonnelByGroups>;
  pauses: WipTreeElement<Pause>;
  distinct_personnel_group: WipTreeElement<DistinctPersonnelGroup>;
}

export interface StoreState {
  WIP: Wip;
  Simulation: Simulation;
}

export type TSimulationMenuOptions =
  | keyof Wip
  | 'scenario_settings'
  | 'operation_steps'
  | 'shutdown_years';

export type TDuplicateType = 'Even' | 'Random';

export type TProcessStepOrder = {
  id: string;
  step: number;
  operation: string;
};
